import { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  ArrowPathIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

export default function ArchiveProspectsGrid({ prospects, refreshProspects }) {
  const [confirmRestore, setConfirmRestore] = useState(null);

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  const handleRestore = async () => {
    if (!confirmRestore) return;
    try {
      await axios.put(`${BACKEND_URL}/prospects/${confirmRestore._id}`, {
        statut: "Prospect",
      });
      refreshProspects();
    } catch (error) {
      console.error("Erreur lors de la restauration :", error);
    }
    setConfirmRestore(null);
  };

  const archivedProspects = prospects.filter((p) => p.statut === "Sans Suite");

  const columns = [
    { field: "nom", headerName: "Nom", width: 150, headerAlign: "center", align: "center" },
    { field: "prenom", headerName: "Prénom", width: 150, headerAlign: "center", align: "center" },
    { field: "email", headerName: "Email", width: 200, headerAlign: "center", align: "center" },
    { field: "telephone", headerName: "Téléphone", width: 170, headerAlign: "center", align: "center" },
    { field: "referent", headerName: "Référent", width: 180, headerAlign: "center", align: "center" },
    { field: "commentaire", headerName: "Commentaires", width: 280, headerAlign: "center", align: "center" },
    {
      field: "actions",
      headerName: "Actions",
      width: 360,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="flex gap-3">
          <button
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-full text-sm shadow transition"
            onClick={() => setConfirmRestore(params.row)}
          >
            <ArrowPathIcon className="w-4 h-4" />
            Restaurer
          </button>
          <button
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-full text-sm shadow transition"
            onClick={() => alert("Suppression définitive non implémentée")}
          >
            <TrashIcon className="w-4 h-4" />
            Supprimer
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white p-6 rounded-xl shadow-md">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Prospects Archivés</h2>
      <div className="h-[66vh] overflow-auto">
        <DataGrid
          rows={archivedProspects}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          checkboxSelection
          getRowId={(row) => row._id}
          className="bg-white"
        />
      </div>

      {/* 🔁 Modale animée pour restauration */}
      <AnimatePresence>
        {confirmRestore && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white p-6 rounded-lg shadow-xl w-[380px]"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800">Restaurer ce prospect ?</h3>
                <button
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => setConfirmRestore(null)}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Êtes-vous sûr de vouloir restaurer <strong>{confirmRestore.nom} {confirmRestore.prenom}</strong> ?
              </p>

              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                  onClick={() => setConfirmRestore(null)}
                >
                  Annuler
                </button>
                <button
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
                  onClick={handleRestore}
                >
                  <CheckCircleIcon className="w-5 h-5" />
                  Confirmer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
